/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import type { Route } from './+types/_app.search';
import { fetchSearchProducts } from '@/lib/api/search.server';
import { getConfig } from '@salesforce/storefront-next-runtime/config';
import { siteContext } from '@salesforce/storefront-next-runtime/site-context';
import { getLogger } from '@/lib/logger.server';
import { PageType } from '@/lib/decorators/page-type';
import { RegionDefinition } from '@/lib/decorators/region-definition';
import { buildCanonicalUrl } from '@/utils/canonical-url';
import { fetchPageWithComponentData } from '@/lib/page-designer/page-loader.server';
import { getInitialFiltersOpen } from '@/hooks/use-filters-panel-state';
/** @sfdc-extension-block-start SNK */
import { SearchPage, type SearchPageData } from '@/pages/search';

export type { SearchPageData };
/** @sfdc-extension-block-end SNK */

@PageType({
    name: 'Search Results Page',
    description: 'Search results page with product listings and personalized content',
    supportedAspectTypes: [],
})
@RegionDefinition([
    {
        id: 'searchTopFullWidth',
        name: 'Top Full Width Region',
        description: 'Full screen width region at the top of search results',
        maxComponents: 5,
    },
    {
        id: 'searchTopContent',
        name: 'Top Content Region',
        description: 'Content width region below sort/filter, above product grid',
        maxComponents: 5,
    },
    {
        id: 'searchBottom',
        name: 'Bottom Region',
        description: 'Region at the bottom of search results after product grid',
        maxComponents: 5,
    },
])
export class SearchPageMetadata {}

/**
 * Server-side loader function that fetches search results data.
 * This function runs on the server during SSR and prepares data for the search page.
 * @returns Object containing search results, refinements, and page metadata
 */
export async function loader(args: Route.LoaderArgs): Promise<SearchPageData> {
    const { context, request } = args;
    const requestUrl = new URL(request.url);
    const { searchParams } = requestUrl;
    const offset = parseInt(searchParams.get('offset') || '0', 10);
    const q = searchParams.get('q') ?? '';
    const sort = searchParams.get('sort') ?? '';
    const refine = searchParams.getAll('refine');
    const initialFiltersOpen = getInitialFiltersOpen(searchParams);
    const config = getConfig(context);
    const logger = getLogger(context);

    const siteCtx = context.get(siteContext);
    if (!siteCtx) {
        logger.error('Search: site context is not available');
        throw new Response('Site context is not available', { status: 500 });
    }
    const { currency } = siteCtx;
    const locale = siteCtx.locale.id;

    const limit = config.search.products.hits.limit;
    const safeCriticalCount = Math.min(config.search.products.hits.critical ?? 2, limit);

    logger.debug('Search: loader starting', { q, offset, sort, refineCount: refine.length });
    const searchResultCritical = await fetchSearchProducts(context, {
        q,
        /** @sfdc-extension-line SNK */
        limit: safeCriticalCount,
        offset,
        sort,
        refine,
        currency,
    });

    // Intentionally await only the above-the-fold products. The remainder streams without delaying the initial
    // document; combining these requests would make the full configured page critical again.
    /** @sfdc-extension-block-start SNK */
    const criticalHitCount = searchResultCritical.hits?.length ?? 0;
    const remainingLimit = Math.max(0, limit - criticalHitCount);
    const searchResultNonCritical =
        remainingLimit > 0
            ? fetchSearchProducts(context, {
                  q,
                  limit: remainingLimit,
                  offset: offset + criticalHitCount,
                  sort,
                  refine,
                  currency,
              })
            : Promise.resolve({
                  ...searchResultCritical,
                  hits: [],
                  offset: offset + criticalHitCount,
                  limit: 0,
              });
    /** @sfdc-extension-block-end SNK */
    const deferredProducts = searchResultNonCritical.then((result) => result.hits ?? []);

    logger.info('Search: results loaded', { query: q, total: searchResultCritical.total, offset });

    const pageUrl = buildCanonicalUrl(requestUrl.origin, requestUrl.pathname, requestUrl.search);
    return {
        searchTerm: q,
        /** @sfdc-extension-line SNK */
        searchResultCritical,
        deferredProducts,
        page: fetchPageWithComponentData(args, {
            pageId: 'search',
        }),
        pageUrl,
        refine,
        currency,
        locale,
        initialFiltersOpen,
        initialCount: Math.min(limit, Math.max(0, (searchResultCritical.total ?? 0) - offset)),
    };
}

export { shouldRevalidate } from '@/lib/revalidation/routes/category';

/** @sfdc-extension-line SNK */
export default SearchPage;
